import Text from "common/Typography/Text";
import FreeOrFreeTrialTag from "features/newMarketplace/common/FreeOrFreeTrialTag";
import styled, { useTheme } from "styled-components";
import useMedia from "utils/hooks/useMedia";
import { DISPLAY_CURRENCIES, MOBILE_SEARCH } from "common/constants";
import React from "react";
import TextWithIcon from "common/Text/TextWithIcon";
import { ICON_TYPES } from "@shared/common/constants";
import { SubGroupOverview, TagName, LanguageNameSpaces } from "@shared/types";
import MarketplaceTag from "features/newMarketplace/common/MarketplaceTag";
import Flex from "common/Boxes/Flex";
import { typography } from "@shared/common/Themes/typography";
import { TrialLabel } from "features/newMarketplace/NewLook/NewLookGroupParts";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { getNewLookTokens, newLookType } from "common/NewLook/tokens.ts";

interface SubGroupPriceProps {
  subGroupOverview: SubGroupOverview;
}

const GroupPrice: React.FC<SubGroupPriceProps> = ({ subGroupOverview }) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.MARKETPLACE);
  const isMobile = useMedia([`(max-width: ${MOBILE_SEARCH})`], [true], false);
  const { isNewLook } = useTheme();

  const price = subGroupOverview.price || 0;
  const discount = subGroupOverview.discount || 0;
  const getDiscountedPrice = () => price - (price * discount) / 100;

  const getFixedPrice = (price: number) => {
    const currency = subGroupOverview.price_currency;
    if (currency === DISPLAY_CURRENCIES.USD) {
      return parseFloat(price.toFixed(2));
    }
    return parseFloat(price.toFixed(6));
  };

  if (isNewLook && subGroupOverview.is_free) {
    return <NewLookAmount>{t("tags.free")}</NewLookAmount>;
  }

  if (isNewLook) {
    const symbol =
      subGroupOverview.price_currency === DISPLAY_CURRENCIES.BTC ? "₿" : "$";
    return (
      <NewLookPrice>
        <NewLookPriceLine>
          <NewLookAmount>
            {symbol}
            {getFixedPrice(getDiscountedPrice())}
          </NewLookAmount>
          <NewLookPer>/ {t("group_info.month_abbr")}</NewLookPer>
          {discount > 0 && (
            <NewLookWas>
              {symbol}
              {getFixedPrice(price)}
            </NewLookWas>
          )}
          <MarketplaceTag tagName={TagName.SALE} group={subGroupOverview} />
        </NewLookPriceLine>
        <TrialLabel
          hasFreeTrial={subGroupOverview.has_free_trial}
          isFree={subGroupOverview.is_free}
        />
      </NewLookPrice>
    );
  }

  return (
    <Flex $flexDirection="column" $alignItems="flex-end" style={CLASSIC_STYLE}>
      <Flex
        $alignItems="baseline"
        style={{
          flexWrap: "wrap",
          justifyContent: "flex-end",
        }}
      >
        <MarketplaceTag tagName={TagName.SALE} group={subGroupOverview} />
        <Flex>
          {discount > 0 && (
            <InitialPrice type="secondary">
              <TextWithIcon
                text={getFixedPrice(price)}
                iconType={ICON_TYPES.DISPLAY_CURRENCY}
                currency={subGroupOverview.price_currency}
              />
              <LineThrough />
            </InitialPrice>
          )}
          <PriceWrapper style={{ marginBottom: isMobile ? 2 : 0 }}>
            <TextWithIcon
              text={`${getFixedPrice(getDiscountedPrice())}/`}
              iconType={ICON_TYPES.DISPLAY_CURRENCY}
              currency={subGroupOverview.price_currency}
              iconStyle={{ fontWeight: typography.fontWeights.SEMI_BOLD }}
              textStyle={{
                fontWeight: typography.fontWeights.SEMI_BOLD,
                fontSize: typography.fontSizes.M,
              }}
            />
            <Text $size={typography.fontSizes.S} style={{ marginLeft: 2 }}>
              {t("group_info.month_abbr")}
            </Text>
          </PriceWrapper>
        </Flex>
      </Flex>
      <FreeOrFreeTrialTag
        hasFreeTrial={subGroupOverview.has_free_trial}
        isFree={subGroupOverview.is_free}
      />
    </Flex>
  );
};

export default GroupPrice;

const CLASSIC_STYLE = { marginRight: 12 };

const NewLookPrice = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;
const NewLookPriceLine = styled.div`
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 4px 8px;
  color: ${(p) => getNewLookTokens(p.theme).ink};
  font-variant-numeric: tabular-nums;
  > :last-child {
    align-self: center;
  }
`;
const NewLookAmount = styled.span`
  ${newLookType.figureLarge};
  color: ${(p) => getNewLookTokens(p.theme).ink};
`;
const NewLookPer = styled.span`
  margin-left: -4px;
  ${newLookType.meta};
  color: ${(p) => getNewLookTokens(p.theme).muted};
`;
const NewLookWas = styled.span`
  ${newLookType.meta};
  color: ${(p) => getNewLookTokens(p.theme).muted};
  text-decoration: line-through;
`;

const PriceWrapper = styled.div`
  display: flex;
  align-items: center;
`;
const InitialPrice = styled(Text).attrs({ as: "div" })`
  margin: 0 4px 0 8px;
  display: flex;
  align-items: center;
  position: relative;
  font-size: ${(p) => p.theme.typography.fontSizes.S};
`;
const LineThrough = styled.div`
  width: 100%;
  height: 1px;
  background: ${(p) => p.theme.colors.secondaryGrey};
  position: absolute;
  top: 10px;
`;
